import React, { useState, useEffect } from "react";
import { 
  DevicePhoneMobileIcon, 
  CloudIcon,
  CpuChipIcon,
  GlobeAltIcon,
  CodeBracketIcon
} from "@heroicons/react/24/outline";

// Real-time software industry portfolio data
const portfolioData = [
    {
      id: 1,
      title: "AI-Powered E-commerce Platform",
      category: "ai",
      client: "TechRetail Corp",
      industry: "E-commerce",
      year: "2024",
      description: "Developed a comprehensive AI-driven e-commerce platform with personalized recommendations, automated inventory management, and predictive analytics.",
      technologies: ["React", "Node.js", "TensorFlow", "AWS", "MongoDB"],
      image: "/images/Appdevelop.png",
      results: {
        revenue: "+45%",
        conversion: "+32%",
        efficiency: "+60%"
      },
      testimonial: {
        text: "UniwayStart transformed  business with AI solutions. The platform has revolutionized customer experience.",
        author: "",
        position: ""
      }
    },
    {
      id: 2,
      title: "Healthcare Management System",
      category: "web",
      client: "MediCare Plus",
      industry: "Healthcare",
      year: "2024",
      description: "Built a secure, HIPAA-compliant healthcare management system with patient records, appointment scheduling, and telemedicine capabilities.",
      technologies: ["React", "Python", "PostgreSQL", "Docker", "Kubernetes"],
      image: "/images/Microservice.png",
      results: {
        patients: "+200%",
        efficiency: "+55%",
        satisfaction: "98%"
      },
      testimonial: {
        text: "The system has streamlined our operations and improved patient care significantly.",
       /* author: "",
        position: ""
        */
      }
    },
    {
      id: 3,
      title: "FinTech Mobile Banking App",
      category: "mobile",
      client: "SecureBank",
      industry: "Financial Services",
      year: "2024",
      description: "Created a secure mobile banking application with biometric authentication, real-time transactions, and AI-powered fraud detection.",
      technologies: ["React Native", "Node.js", "Blockchain", "AWS", "Redis"],
      image: "/images/Appdevelop2.png",
      results: {
        users: "+150%",
        security: "99.9%",
        rating: "4.8/5"
      },
      testimonial: {
        text: "Outstanding security and user experience. Our customers love the new app.",
       /* author: "Jennifer Martinez",
        position: "CTO, SecureBank"*/
      }
    },
    {
      id: 4,
      title: "IoT Smart Manufacturing Platform",
      category: "ai",
      client: "ManufacturePro",
      industry: "Manufacturing",
      year: "2023",
      description: "Developed an IoT-powered smart manufacturing platform with real-time monitoring, predictive maintenance, and quality control automation.",
      technologies: ["Python", "IoT", "Machine Learning", "Azure", "Time Series DB"],
      image: "/images/Advisory.png",
      results: {
        downtime: "-70%",
        quality: "+40%",
        cost: "-25%"
      },
      testimonial: {
        text: "The platform has revolutionized our manufacturing process with incredible efficiency gains.",
     /*   author: "Robert Thompson",
        position: "Operations Director, ManufacturePro"*/
      }
    },
    {
      id: 5,
      title: "Cloud Migration & Microservices",
      category: "cloud",
      client: "",
      industry: "Technology",
      year: "2023",
      description: "Migrated legacy monolithic applications to cloud-native microservices architecture with 99.9% uptime and auto-scaling capabilities.",
      technologies: ["Kubernetes", "Docker", "AWS", "Microservices", "CI/CD"],
      image: "/images/slide-1.png",
      results: {
        performance: "+300%",
        scalability: "+500%",
        cost: "-40%"
      },
      testimonial: {
        text: "The migration exceeded our expectations. Our systems are now more reliable and cost-effective.",
      /*  //author: "David Wilson",
        //position: "VP Engineering, GlobalTech Solutions"*/
      }
    },
    {
      id: 6,
      title: "EdTech Learning Management System",
      category: "web",
      client: "",
      industry: "Education",
      year: "2023",
      description: "Built a comprehensive LMS with AI-powered personalized learning paths, virtual classrooms, and progress analytics.",
      technologies: ["Vue.js", "Laravel", "AI/ML", "WebRTC", "Analytics"],
      image: "/images/slide-4.png",
      results: {
        engagement: "+80%",
        completion: "+65%",
        satisfaction: "96%"
      },
      testimonial: {
        text: "Students are more engaged and learning outcomes have improved dramatically.",
       /* author: "Dr. Emily Rodriguez",
        position: "Academic Director, EduFuture Academy"*/
      }
    }
];

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    setProjects(portfolioData);
  }, []);

  const categories = [
    { id: 'all', name: 'All Projects', icon: GlobeAltIcon },
    { id: 'ai', name: 'AI Solutions', icon: CpuChipIcon },
    { id: 'web', name: 'Web Development', icon: CodeBracketIcon },
    { id: 'mobile', name: 'Mobile Apps', icon: DevicePhoneMobileIcon },
    { id: 'cloud', name: 'Cloud Solutions', icon: CloudIcon }
  ];

  const filteredProjects = activeTab === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeTab);

  return (
    <section className="py-20 bg-gray-50" id="portfolio">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Portfolio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how we&apos;ve helped businesses across industries transform digitally with cutting-edge technology solutions.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeTab === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                <Icon className="w-5 h-5" />
                {category.name}
              </button>
            );
          })}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.year}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                    {project.industry}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {project.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Results */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {Object.entries(project.results).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-lg font-bold text-blue-600">{value}</div>
                      <div className="text-xs text-gray-500 capitalize">{key}</div>
                    </div>
                  ))}
                </div>

                {/* Testimonial */}
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 italic mb-2">
                    &ldquo;{project.testimonial.text}&rdquo;
                  </p>
                  <div className="text-xs text-gray-500">
                    <div className="font-medium">{project.testimonial.author}</div>
                    <div>{project.testimonial.position}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-20 bg-white rounded-2xl shadow-lg p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">20+</div>
              <div className="text-gray-600">Projects Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">5+</div>
              <div className="text-gray-600">Happy Clients</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">90%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">Support</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
